import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, apiError } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, MapPin, Megaphone, Plus, Pencil, Trash2, Loader2, ImageUp, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const fmtData = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "");
export const notiziaImg = (n) => (n.has_image ? `${API}/public/notizie/${n.id}/image?v=${encodeURIComponent(n.image_v || "")}` : null);

export const useNotizie = (limit) => {
  const [items, setItems] = useState(null);
  const reload = useCallback(() => {
    api.get("/public/notizie", { params: limit ? { limit } : {} }).then((r) => { setItems(r.data || []); }).catch(() => { setItems([]); });
  }, [limit]);
  useEffect(() => { reload(); }, [reload]);
  return { items, reload };
};

const EMPTY = { tipo: "comunicato", titolo: "", testo: "", data_evento: "", luogo: "" };

export const NotiziaDialog = ({ notizia, onClose, onSaved }) => {
  const [d, setD] = useState(notizia ? { ...EMPTY, ...notizia } : EMPTY);
  const [image, setImage] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const preview = image || (!removeImage && notizia ? notiziaImg(notizia) : null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) { setImage(await resizeImage(f, 1600, 1600, 0.82)); setRemoveImage(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const body = { tipo: d.tipo, titolo: d.titolo, testo: d.testo, data_evento: d.data_evento, luogo: d.luogo, image, remove_image: removeImage };
      if (notizia) { await api.put(`/notizie/${notizia.id}`, body); } else { await api.post("/notizie", body); }
      toast.success(notizia ? "Notizia aggiornata" : "Notizia pubblicata");
      onSaved();
      onClose();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="notizia-dialog">
        <DialogHeader><DialogTitle>{notizia ? "Modifica notizia" : "Nuova notizia"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1">
            {[["comunicato", "Comunicato"], ["evento", "Evento"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setD({ ...d, tipo: k })} data-testid={`notizia-tipo-${k}`}
                className={`rounded-md py-2 text-xs font-semibold ${d.tipo === k ? "bg-white text-[#14382C] shadow-sm" : "text-slate-500"}`}>{l}</button>
            ))}
          </div>
          <div className="space-y-1"><Label className="text-xs">Titolo *</Label><Input value={d.titolo} onChange={set("titolo")} data-testid="notizia-titolo" /></div>
          {d.tipo === "evento" && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label className="text-xs">Data dell'evento</Label><Input type="date" value={d.data_evento} onChange={set("data_evento")} data-testid="notizia-data" /></div>
              <div className="space-y-1"><Label className="text-xs">Luogo</Label><Input value={d.luogo} onChange={set("luogo")} data-testid="notizia-luogo" /></div>
            </div>
          )}
          <div className="space-y-1"><Label className="text-xs">Testo</Label><Textarea rows={8} value={d.testo} onChange={set("testo")} data-testid="notizia-testo" /></div>
          <div className="space-y-2">
            <Label className="text-xs">Foto</Label>
            {preview && <img src={preview} alt="Anteprima" className="max-h-48 rounded-md object-cover" data-testid="notizia-preview" />}
            <div className="flex flex-wrap gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium hover:bg-slate-200">
                <ImageUp className="h-4 w-4" /> {preview ? "Cambia foto" : "Aggiungi foto"}
                <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="notizia-image-input" />
              </label>
              {preview && <Button type="button" variant="ghost" size="sm" className="text-red-700" onClick={() => { setImage(null); setRemoveImage(true); }} data-testid="notizia-image-remove">Togli foto</Button>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !d.titolo.trim()} className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="notizia-save">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {notizia ? "Salva" : "Pubblica"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TipoBadge = ({ n }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${n.tipo === "evento" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
    {n.tipo === "evento" ? <CalendarDays className="h-3 w-3" /> : <Megaphone className="h-3 w-3" />} {n.tipo === "evento" ? "Evento" : "Comunicato"}
  </span>
);

export const NotiziaMeta = ({ n }) => (
  <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
    <TipoBadge n={n} />
    {n.tipo === "evento" && n.data_evento ? <span className="font-semibold text-amber-700">{fmtData(n.data_evento)}</span> : <span>{fmtData(n.published_at)}</span>}
    {n.tipo === "evento" && n.luogo && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {n.luogo}</span>}
  </div>
);

export const NotiziaCard = ({ n, i, edit, onEdit, onDelete }) => (
  <article data-testid={`notizia-card-${i}`} className="group relative flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-lg">
    <Link to={`/sito/notizie/${n.id}`} className="block aspect-[16/10] overflow-hidden bg-[#14382C]" data-testid={`notizia-link-${i}`}>
      {notiziaImg(n) ? <img src={notiziaImg(n)} alt={n.titolo} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        : <div className="flex h-full items-center justify-center"><img src="/emblem.png" alt="" className="h-20 opacity-60" /></div>}
    </Link>
    <div className="flex flex-1 flex-col gap-3 p-5">
      <NotiziaMeta n={n} />
      <Link to={`/sito/notizie/${n.id}`} className="font-heading text-lg font-bold leading-snug text-slate-900 hover:text-[#166534]">{n.titolo}</Link>
      <p className="line-clamp-3 text-sm text-slate-600">{n.testo}</p>
      <Link to={`/sito/notizie/${n.id}`} className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-[#166534]">Leggi <ArrowRight className="h-4 w-4" /></Link>
    </div>
    {edit && (
      <div className="absolute right-3 top-3 flex gap-1">
        <Button size="icon" className="h-8 w-8 bg-white text-[#14382C] hover:bg-slate-100" onClick={() => onEdit(n)} data-testid={`notizia-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" className="h-8 w-8 bg-white text-red-700 hover:bg-slate-100" onClick={() => onDelete(n)} data-testid={`notizia-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    )}
  </article>
);

export const useNotiziaActions = (reload) => {
  const [editing, setEditing] = useState(undefined);
  const remove = async (n) => {
    if (!window.confirm(`Eliminare "${n.titolo}"?`)) { return false; }
    try { await api.delete(`/notizie/${n.id}`); toast.success("Notizia eliminata"); reload(); return true; } catch { toast.error("Eliminazione non riuscita"); return false; }
  };
  const dialog = editing !== undefined ? <NotiziaDialog notizia={editing} onClose={() => setEditing(undefined)} onSaved={reload} /> : null;
  return { openNew: () => setEditing(null), openEdit: (n) => setEditing(n), remove, dialog };
};

export const NewNotiziaButton = ({ onClick }) => (
  <Button onClick={onClick} className="bg-[#D97706] hover:bg-[#b45309]" data-testid="notizia-new"><Plus className="mr-2 h-4 w-4" /> Nuova notizia</Button>
);
